import { Immutable } from "immer";
import React from "react";
import { t } from "ttag";

import { usePageSetting } from "../../utils/settings";
import { CaptchaLibrary, getCaptchaToken } from "../Captcha";
import {
    FORM_ERROR_CLASS_NAME,
    FORM_ERROR_LIST_CLASS_NAME,
    FORM_FIELD_CLASS_NAME,
} from "../classNames";

/**
 * Standalone reCAPTCHA v3 field for full-document React form pages.
 *
 * The Django `ReCaptchaV3` widget cannot be serialized by reactivated (it is
 * not a registered widget), so the captcha is excluded from the reactivated
 * form (see `ContactFormRenderable`) and rendered here instead. The token is
 * populated on submit by `useReCaptchaSubmit`. The grecaptcha library is
 * injected client-side (via
 * `CaptchaLibrary`'s effect), so there is no SSR `<head>` script to mismatch on
 * hydration. The full `ContactForm` (with captcha) still validates the token
 * server-side on POST.
 */
export const ReactivatedReCaptchaField = (
    props: Immutable<{
        id?: string;
        labelText?: string;
        fieldClassName?: string;
        // Server-side captcha validation errors (the captcha field is excluded
        // from the serialized form, so its errors can't ride form.errors).
        // Rendered as the same error list Django's field loop produced
        // (`<ul id="captcha-errors" class="form__error-list">`), inside the
        // field wrapper. Omitted/empty renders nothing.
        errors?: string[];
    }>,
): React.JSX.Element => {
    const {
        id = "id_captcha",
        labelText: labelTextProp,
        // A caller-supplied `fieldClassName` replaces the default wholesale.
        fieldClassName = FORM_FIELD_CLASS_NAME,
        errors,
    } = props;
    // Resolve the translated fallback in the render body (not as a destructuring
    // default, which is evaluated once at definition time) so `t` runs per render
    // with the active locale context.
    const labelText = labelTextProp ?? t`Captcha`;
    const sitekey = usePageSetting("google_recaptcha_sitekey");
    return (
        <div className={fieldClassName} data-testid="form-field">
            <CaptchaLibrary />
            <input
                type="hidden"
                className="g-recaptcha"
                id={id}
                name="captcha"
                data-sitekey={sitekey || undefined}
            />
            <label htmlFor={id}>{labelText}</label>
            {errors && errors.length > 0 && (
                <ul
                    id="captcha-errors"
                    className={FORM_ERROR_LIST_CLASS_NAME}
                    data-testid="form-error-list"
                >
                    {errors.map((error, i) => (
                        <li
                            className={FORM_ERROR_CLASS_NAME}
                            data-testid="form-error"
                            key={i}
                        >
                            {error}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

/**
 * Returns an `onSubmit` handler for a full-page-POST form that injects a fresh
 * reCAPTCHA v3 token into the form's hidden `captcha` field before submitting.
 *
 * On first submit it cancels the native submit, fetches a token, writes it into
 * the `captcha` input, then re-submits via `requestSubmit()`. The second pass
 * (guarded by the ref) is allowed through so the browser performs the real POST
 * and any other submit listeners (e.g. KUID analytics) still run.
 */
export const useReCaptchaSubmit = (
    action: string,
): ((e: React.FormEvent<HTMLFormElement>) => void) => {
    const resubmitting = React.useRef(false);
    return React.useCallback(
        (e: React.FormEvent<HTMLFormElement>) => {
            if (resubmitting.current) {
                resubmitting.current = false;
                return;
            }
            e.preventDefault();
            const formEl = e.currentTarget;
            void (async () => {
                // A grecaptcha network/runtime failure must not strand the
                // submit: swallow it and proceed with an empty token. The
                // native submit was already cancelled, so without the
                // re-submit the button would silently do nothing; the full
                // ContactForm validates the token server-side and surfaces a
                // captcha error on an empty/invalid token.
                let token: string | null = null;
                try {
                    token = await getCaptchaToken(action);
                } catch {
                    token = null;
                }
                const input = formEl.querySelector<HTMLInputElement>(
                    'input[name="captcha"]',
                );
                if (input) {
                    input.value = token ?? "";
                }
                resubmitting.current = true;
                formEl.requestSubmit();
            })();
        },
        [action],
    );
};
